import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToId } from "@/App";

const LINKS = [
  { id: "services", label: "Services" },
  { id: "pricing", label: "Pricing" },
  { id: "process", label: "Process" },
  { id: "about", label: "About" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-edge" : "bg-transparent border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="/" data-testid="nav-logo-link" onClick={(e) => { e.preventDefault(); window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <Logo />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}-link`}
              onClick={(e) => go(e, l.id)}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-cyanbrand transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/catalog"
            data-testid="nav-catalog-link"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-cyanbrand transition-colors"
          >
            Catalog
          </a>
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="/account"
            data-testid="nav-account-link"
            className="font-mono text-[11px] tracking-[0.2em] text-mist hover:text-cyanbrand transition-colors"
          >
            SIGN IN
          </a>
          <button
            data-testid="nav-quote-button"
            onClick={(e) => go(e, "contact")}
            className="group inline-flex items-center gap-2 bg-paper text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-cyanbrand transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={2.5} />
            Get a Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-paper p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-edge"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  data-testid={`nav-mobile-${l.id}-link`}
                  onClick={(e) => go(e, l.id)}
                  className="font-mono text-xs uppercase tracking-[0.25em] text-mist hover:text-cyanbrand"
                >
                  {l.label}
                </a>
              ))}
              <a href="/catalog" data-testid="nav-mobile-catalog-link" className="font-mono text-xs uppercase tracking-[0.25em] text-mist hover:text-cyanbrand">
                CATALOG
              </a>
              <a href="/account" data-testid="nav-mobile-account-link" className="font-mono text-xs uppercase tracking-[0.25em] text-cyanbrand">
                SIGN IN / SIGN UP
              </a>
              <button
                data-testid="nav-mobile-quote-button"
                onClick={(e) => go(e, "contact")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-paper text-ink font-semibold text-sm px-5 py-3 rounded-sm"
              >
                <Phone size={15} strokeWidth={2.5} /> Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
